<script>
export default {
  title: 'Project 2: Hashing Exercises',
  starterId: 'proj2.zip'
}
</script>

<template>
  <section>
    <article>
      <h2>Reading</h2>
      <ul>
        <li><cite>Data Structures and Problem Solving using Java (4th Edition)</cite> Chapter 20</li>
      </ul>
    </article>

    <article>
      <h2>Miscellaneous Information</h2>

      <p>
        In this course, we are concerned only with chained hash tables.
        Techniques discussed in <cite>Data Structures and Problem Solving using
        Java (4th Edition)</cite> Sections 20.3 - 20.4 that store colliding
        elements elsewhere in the table are not used frequently enough to be of
        interest here. Examples here use the <code>java.util.Hashtable</code>
        class.
      </p>

      <p>
        There are two potentially confusing aspects of the
        <code>java.util.Hashtable</code> class.
      </p>

      <ul>
        <li>
          <p>It stores only objects for which the <code>hashCode</code> and
            <code>equals</code> methods have been overridden. In particular, the
            signature of the <code>equals</code> method must be</p>

          <pre v-highlightjs><code class="java">public boolean equals(Object obj)</code></pre>

          <p>so the code within the <code>equals</code> method will probably
            include a cast.</p>
        </li>
        <li>
          It associates <dfn>keys</dfn> with <dfn>values</dfn>, so the <code>put</code>
          method takes two objects as arguments, a key and its associated value. To use
          a <code>HashTable</code> object merely to store a set, represent the set elements
          as keys and choose some arbitrary value to go with each key.
        </li>
      </ul>

      <p>Two alternatives to <code>Hashtable</code> are <code>HashMap</code>
        (similar to <code>Hashtable</code>) and <code>HashSet</code>. Consult the
        Java API for more information about these classes. You may use them instead
        of <code>Hashtable</code> if you wish.</p>
    </article>

    <article>
      <h2>Starter Code</h2>

      <p>
        Please download the starter code from above. It should contains three folders
        containing starter code for exercise 1 to 3. There is no starter code for exercise 0.
      </p>
    </article>

    <!--<article>-->
      <!--<h2>Goals</h2>-->
      <!--<p>-->
        <!--This assignment gives you practice devising and evaluating hash-->
        <!--functions, and using the <code>java.util.Hashtable</code> class.-->
      <!--</p>-->
    <!--</article>-->

    <article>
      <h2>Exercise 1</h2>

      <p><b>(You do not need to write any code for this exercise)</b></p>

      <p>The following questions are meant to test your understanding of a hash map's
        internal structure, and what makes a good hash function. Turn in your diagrams
        to a tutor and answer the additional questions.</p>

      <p>
        Your mathematician friend has read about hash maps,
        and naively made this class, which represents numbers mod 3.
      </p>

      <pre v-highlightjs><code class="java">public class NumberMod3() {
    int num;
    public NumberMod3(int n){
        num = n;
    }
    public int hashCode() {
        return num * 3;
    }
    public boolean equals(NumberMod3 other) {
        return num % 3 == other.getInt() % 3;
    }
    public int getInt(){
        return num;
    }
}</code></pre>

      <p>
        Assume he declares map to be a <code>HashMap&lt;NumberMod3, Integer&gt;</code>,
        and initializes it to have <b>an initial size
        of 8</b> and to have <b>a load factor of 1/2</b>. This means it resizes
        whenever it hits more than N / 2 elements in the table, where
        N (initially 8) is the current size the of the table.
        Assume further the table uses linked-list chaining and has a
        <b>resize factor of 2</b> (doubles in size whenever resized).
      </p>

      <ol>
        <li>
          <p>
            <b>Draw TWO diagrams</b> of the internal structure of the hash map after these
            operations.
          </p>

          <pre v-highlightjs><code class="java">map.put(new numberMod3(1), 1)
map.put(new numberMod3(2), 2)
/* DIAGRAM 1: diagram of hash map at this point */
map.put(new numberMod3(4), 4)
map.put(new numberMod3(5), 5)
map.put(new numberMod3(6), 6)
/* DIAGRAM 2: diagram of hash map at this point */</code></pre>

          <p>The following diagram is an example of how a hashmap of size 5 may look like.</p>

          <figure>
            <img class="img-responsive center-block" src="./images/hashmap.png" width="500">
          </figure>
        </li>
        <li>
          <p>
            As a veteran java user, you realize some potential problems with the
            <code>hashCode()</code> function and <code>equals()</code> function. <b>Give
            an example</b> of how <code>hashCode()</code> could be ineffective, and <b>an
            example</b> of how <code>equals()</code> could be ineffective, and suggest
            improvements. (hint: what are some values of initialization when the hash
            map would be ineffective?)
          </p>
        </li>
        <li>
          <p>
            Additional checkoff questions:
          </p>
          <ul>
            <li>What is the worst-case runtime of a single get and put operation?</li>
            <li>What is the average case runtime? Be prepared to explain why, and how resizing affects it.</li>
            <li>What are some properties of a good hash function?</li>
          </ul>
        </li>
      </ol>
    </article>



    <article>
      <h2>Exercise 2</h2>

      <p>The code in <code>TTThashTest.java</code> stores all possible Tic-tac-toe boards
        into a <code>java.util.Hashtable</code> object. <b>Add two versions of hashCode method</b> to the <code>TTTboard</code>
        class in <code>TTTboard.java</code> that <b>test two ways</b> to hash Tic-tac-toe boards:
      </p>

      <ul>
        <li>In one, you should convert the board to a <code>String</code> and then use the
          <code>String hashCode()</code> function.</li>
        <li>In the other, you should interpret the
          Tic-tac-toe board as a nine-digit base 3 numeral and return the corresponding integer
          as the hash value. Compare the performance of these two implementations.</li>
      </ul>

      <p>In many Java environments, <code>String</code> methods are "native",
        that is, they are supplied in the machine language of the computer on which
        the program is executing. The data you collect in this exercise should
        indicate whether or not this is the case on the computer you're using.</p>

      <p><b>Report your results</b> of the two hash functions and try to <b>explain</b> what might cause the difference.</p>
    </article>

    <article>
      <h2>Exercise 3</h2>

      <p>Two CS 47B students have a jug filled with eight liters of Jolt Cola
        that they wish to divide evenly between them. They have two other empty jugs with
        capacities of five and three liters respectively (therefore three jugs in total).
        These jugs are unmarked and no other measuring device is available. How can they
        accomplish the equal division?</p>

      <p>The file <code>JugSolver.java</code> contains part of a program to solve this problem.
        The program recursively searches to find a sequence of steps (each step pours one jug
        into another) that produces the desired amount. The program has a flaw, namely that it
        doesn't keep track of configurations that it has seen before; infinite recursion results.</p>

      <p>
        Without changing any of the existing code (except perhaps to change the
        value of the <code>DEBUGGING</code> variable), <b>add statements that fix the
        program</b>. In particular, you are to use a <code>java.util.Hashtable</code>
        object to keep track of configurations already seen. </p>

    <p><b>Requirements:</b></p>
      <ul>
        <li>
          <p><b>Test</b> your completed program on the following arguments, which correspond to the problem stated above.</p>

          <pre v-highlightjs><code class="bash">8 5 3 8 0 4</code></pre>

        </li>
        <li>
          <p><b>Indicate</b>, on the program listing you bring to the Self-Paced Center
          for grading, what you added to the framework code.</p>
        </li>

        <li>
          <p><b>Explain</b> to a tutor about the entire program, what is the meaning/function of each variable and method,
          including those already provided from the starter code.</p>
        </li>
      </ul>
    </article>

    <article>
      <h2>Checklist</h2>
      <ul>
        <li>Correct solutions to all three exercises:
          <ul>
            <li>evaluation of the four hash functions in <code>Hashtable.java</code>;</li>
            <li>comparison of hash functions for Tic-tac-toe boards;</li>
            <li>use of a <code>java.util.Hashtable</code> object to help solve the jug-pouring problem.</li>
          </ul>
        </li>
        <li>Transcripts of test results.</li>
        <li>Listings of your programs, indented to show their structure, accompanied by comments that describe their purpose and arguments.</li>
        <li>Reasonable names for functions and parameters.</li>
      </ul>
    </article>
  </section>

</template>



// WEBPACK FOOTER //
// src/components/assignments/47b/HashingExercises.vue